import React, { useState, useEffect, useMemo } from 'react';
import { PageShell } from '../../components/Ui';
import { useHmsRecords } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   PHYSIOTHERAPY MODULE
   - Patient Referral & Assessment
   - Treatment Plan Builder
   - Session Log
   - Outcome Tracking (Pain VAS, ROM, Functional Score)
   - Exercise Prescription Library
   - Equipment Booking
   - Department Statistics
═══════════════════════════════════════════════════════════ */

const EXERCISE_LIBRARY = [
  { name: 'Ankle Pumps', category: 'Lower Limb', reps: '20 reps × 3 sets', notes: 'For DVT prophylaxis post-surgery' },
  { name: 'Straight Leg Raise (SLR)', category: 'Lower Limb', reps: '10 reps × 3 sets', notes: 'Knee strengthening' },
  { name: 'Quad Sets', category: 'Lower Limb', reps: '10 reps × 3 sets', notes: 'Isometric quad strengthening' },
  { name: 'Shoulder Pendulum', category: 'Upper Limb', reps: '2 min × 3 sets', notes: 'Post-shoulder surgery' },
  { name: 'Shoulder Wheel', category: 'Upper Limb', reps: '5 min', notes: 'ROM improvement' },
  { name: 'Deep Breathing Exercises', category: 'Respiratory', reps: '10 breaths × 5 sets', notes: 'Post-op respiratory physio' },
  { name: 'Incentive Spirometry', category: 'Respiratory', reps: '10 reps/hr while awake', notes: 'Prevent atelectasis ICU' },
  { name: 'Chest Physiotherapy (CPT)', category: 'Respiratory', reps: '15 min BD', notes: 'For secretion clearance' },
  { name: 'Neck ROM Exercises', category: 'Spine', reps: '10 reps each direction', notes: 'Cervical spondylosis' },
  { name: 'McKenzie Extension', category: 'Spine', reps: '10 reps × 3 sets', notes: 'Lumbar disc prolapse' },
  { name: 'Pelvic Floor Exercises (Kegel)', category: 'Pelvic', reps: '10 sec hold × 10 reps', notes: 'Post-partum / incontinence' },
  { name: 'Balance Training', category: 'Neurological', reps: '10 min', notes: 'Stroke rehabilitation' },
  { name: 'Gait Training', category: 'Neurological', reps: '15–20 min', notes: 'Post-stroke / post-op ambulation' },
  { name: 'Ultrasound Therapy', category: 'Electrotherapy', reps: '5–10 min', notes: 'Soft tissue injury' },
  { name: 'TENS (Transcutaneous ES)', category: 'Electrotherapy', reps: '20 min', notes: 'Pain management' },
  { name: 'IFT (Interferential Therapy)', category: 'Electrotherapy', reps: '15 min', notes: 'Joint pain, muscle spasm' },
  { name: 'Hot Pack', category: 'Physical Modalities', reps: '20 min', notes: 'Muscle relaxation before exercises' },
  { name: 'Ice Pack (Cryotherapy)', category: 'Physical Modalities', reps: '15 min', notes: 'Acute inflammation reduction' },
];

function AssessmentTab({ patient, onSave }) {
  const [form, setForm] = useState({ painVas: patient?.painVas || 5, rom: '', mmrc: '', bmi: '', bp: '', spo2: '', notes: '', physio: '', goals: patient?.goal || '' });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!onSave) return;
    setBusy(true);
    try {
      await onSave({ patientId: patient?.id || patient?.recordId, patientName: patient?.name, ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit} style={{ borderLeft: '4px solid #0d6e6a' }}>
      <h3 style={{ marginTop: 0 }}>📋 Physiotherapy Assessment — {patient?.name}</h3>
      {saved && <div className="info-banner mb">✅ Assessment saved successfully!</div>}
      <div style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, marginBottom: 14 }}>
        <div><strong>Diagnosis:</strong> {patient?.diagnosis}</div>
        <div><strong>Referred by:</strong> {patient?.referredBy}</div>
        <div><strong>Treatment Goal:</strong> {patient?.goal}</div>
      </div>
      <div className="form-grid mb">
        <div className="field full">
          <label>Pain Score (VAS 0–10)</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
              <button key={n} type="button" onClick={() => setForm(f => ({ ...f, painVas: n }))} style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid', borderColor: form.painVas === n ? (n <= 3 ? '#10b981' : n <= 6 ? '#f59e0b' : '#dc2626') : '#e2e8f0', background: form.painVas === n ? (n <= 3 ? '#10b981' : n <= 6 ? '#f59e0b' : '#dc2626') : '#fff', color: form.painVas === n ? '#fff' : '#475569', fontWeight: 900, cursor: 'pointer', fontSize: 14, transition: 'all 0.15s' }}>{n}</button>
            ))}
            <span style={{ fontWeight: 700, color: form.painVas <= 3 ? '#10b981' : form.painVas <= 6 ? '#f59e0b' : '#dc2626' }}>{form.painVas <= 3 ? 'Mild' : form.painVas <= 6 ? 'Moderate' : 'Severe'}</span>
          </div>
        </div>
        <div className="field"><label>Range of Motion (degrees)</label><input className="input" value={form.rom} onChange={e => setForm(f => ({ ...f, rom: e.target.value }))} placeholder="e.g. Shoulder Flex: 120° / Abd: 90°" /></div>
        <div className="field"><label>mMRC Dyspnoea Scale</label><select className="input" value={form.mmrc} onChange={e => setForm(f => ({ ...f, mmrc: e.target.value }))}><option value="">Select</option>{[0, 1, 2, 3, 4].map(n => <option key={n} value={n}>Grade {n}</option>)}</select></div>
        <div className="field"><label>BP (mmHg)</label><input className="input" value={form.bp} onChange={e => setForm(f => ({ ...f, bp: e.target.value }))} placeholder="120/80" /></div>
        <div className="field"><label>SpO₂ (%)</label><input type="number" className="input" value={form.spo2} onChange={e => setForm(f => ({ ...f, spo2: e.target.value }))} /></div>
        <div className="field"><label>Physiotherapist</label><input className="input" value={form.physio} onChange={e => setForm(f => ({ ...f, physio: e.target.value }))} placeholder="Dr. Name" /></div>
        <div className="field full"><label>Treatment Goals</label><input className="input" value={form.goals} onChange={e => setForm(f => ({ ...f, goals: e.target.value }))} /></div>
        <div className="field full"><label>Assessment Notes</label><textarea className="input" rows={4} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Functional assessment, posture, gait analysis, muscle strength, neurological status..." /></div>
      </div>
      <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : '✓ Save Assessment'}</button></div>
    </form>
  );
}

function TreatmentPlanTab({ patient, savedPlans, onSavePlan }) {
  const patientId = patient?.id || patient?.recordId;
  const latestPlan = useMemo(() => {
    const forPatient = (savedPlans || []).filter((p) => p.patientId === patientId);
    return [...forPatient].sort((a, b) => (b.savedAt || b.createdAt || '').localeCompare(a.savedAt || a.createdAt || ''))[0];
  }, [savedPlans, patientId]);
  const [plan, setPlan] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [busy, setBusy] = useState(false);
  const exerciseCategories = [...new Set(EXERCISE_LIBRARY.map(e => e.category))];

  useEffect(() => {
    setPlan(latestPlan?.plan || []);
  }, [latestPlan?.recordId, patientId]);

  if (!patient) return <p className="hint">Select a patient to manage treatment plan.</p>;

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => setShowLibrary(s => !s)}>{showLibrary ? '✕ Close Library' : '📚 Exercise Library'}</button>
      </div>
      {showLibrary && (
        <div className="card mb">
          <h4 style={{ marginTop: 0 }}>Exercise Prescription Library</h4>
          {exerciseCategories.map(cat => (
            <div key={cat} style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{cat}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {EXERCISE_LIBRARY.filter(e => e.category === cat).map(ex => (
                  <button key={ex.name} type="button" onClick={() => { setPlan(p => [...p, { id: Date.now(), exercise: ex.name, reps: ex.reps, freq: 'Daily', weeks: 2, done: false }]); setShowLibrary(false); }} style={{ padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ {ex.name}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Treatment Plan — {patient?.name}</h3>
        {plan.map((item, i) => (
          <div key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
            <input type="checkbox" checked={item.done} onChange={() => setPlan(p => p.map((x, j) => j === i ? { ...x, done: !x.done } : x))} style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1, opacity: item.done ? 0.5 : 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, textDecoration: item.done ? 'line-through' : 'none' }}>{item.exercise}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{item.reps} · {item.freq} · {item.weeks} weeks</div>
            </div>
            <button type="button" style={{ fontSize: 11, padding: '2px 8px', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 5, cursor: 'pointer' }} onClick={() => setPlan(p => p.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-primary btn-sm" disabled={busy || !plan.length} onClick={async () => {
            if (!onSavePlan || !patient) return;
            setBusy(true);
            try {
              await onSavePlan({ patientId, patientName: patient.name, plan });
            } finally { setBusy(false); }
          }}>{busy ? 'Saving…' : '✓ Save Plan'}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Print Plan</button>
        </div>
        {plan.length === 0 && <p className="hint mt">No exercises in plan — add from the exercise library.</p>}
      </div>
    </div>
  );
}

function SessionLogTab({ patient, sessions, onCreateSession, onPatchPatient }) {
  const patientId = patient?.id || patient?.recordId;
  const patientSessions = useMemo(() => (
    (sessions || [])
      .filter((s) => s.patientId === patientId)
      .sort((a, b) => (b.sessionAt || b.createdAt || '').localeCompare(a.sessionAt || a.createdAt || ''))
  ), [sessions, patientId]);
  const [form, setForm] = useState({ exercises: '', durationMin: 30, painVasAfter: 5, therapist: '', notes: '' });
  const [busy, setBusy] = useState(false);

  if (!patient) return <p className="hint">Select a patient to log sessions.</p>;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.exercises.trim()) return;
    setBusy(true);
    try {
      await onCreateSession({
        patientId,
        patientName: patient.name,
        ...form,
        durationMin: Number(form.durationMin) || 30,
        painVasAfter: Number(form.painVasAfter),
        sessionAt: new Date().toISOString(),
      });
      const nextSessions = (patient.sessions || 0) + 1;
      await onPatchPatient?.(patient.recordId || patient.id, {
        sessions: nextSessions,
        painVas: Number(form.painVasAfter),
        lastSessionAt: new Date().toISOString(),
      });
      setForm({ exercises: '', durationMin: 30, painVasAfter: 5, therapist: '', notes: '' });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <form className="card mb" onSubmit={submit} style={{ borderLeft: '4px solid #3b82f6' }}>
        <h3 style={{ marginTop: 0, color: '#2563eb' }}>📝 Log Session — {patient.name}</h3>
        <div className="form-grid mb">
          <div className="field full"><label>Exercises / Modalities Done *</label><input className="input" required value={form.exercises} onChange={(e) => setForm((f) => ({ ...f, exercises: e.target.value }))} placeholder="e.g. SLR, ankle pumps, TENS 15 min" /></div>
          <div className="field"><label>Duration (min)</label><input type="number" className="input" value={form.durationMin} onChange={(e) => setForm((f) => ({ ...f, durationMin: e.target.value }))} /></div>
          <div className="field"><label>Pain VAS After (0–10)</label><input type="number" min={0} max={10} className="input" value={form.painVasAfter} onChange={(e) => setForm((f) => ({ ...f, painVasAfter: e.target.value }))} /></div>
          <div className="field"><label>Physiotherapist</label><input className="input" value={form.therapist} onChange={(e) => setForm((f) => ({ ...f, therapist: e.target.value }))} /></div>
          <div className="field full"><label>Session Notes</label><textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : '✓ Save Session'}</button>
      </form>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Session History ({patientSessions.length})</h3>
        {patientSessions.length === 0 && <p className="hint">No sessions logged yet.</p>}
        {patientSessions.map((s) => (
          <div key={s.recordId || s.id} className="card mb" style={{ padding: 12, borderLeft: '3px solid #3b82f6' }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{new Date(s.sessionAt || s.createdAt).toLocaleString('en-IN')}</div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>{s.exercises}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{s.durationMin} min · VAS after: {s.painVasAfter}/10 · {s.therapist || '—'}</div>
            {s.notes && <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{s.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PhysiotherapyPage() {
  const { records: patients, patch: patchPatient, create: createPatient } = useHmsRecords('physio-patient');
  const { create: createAssessment } = useHmsRecords('physio-assessment');
  const { records: plans, create: createPlan } = useHmsRecords('physio-plan');
  const { records: sessions, create: createSession } = useHmsRecords('physio-session');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [subTab, setSubTab] = useState('assessment');
  const [showReferral, setShowReferral] = useState(false);
  const [referralForm, setReferralForm] = useState({ name: '', diagnosis: '', referredBy: '', goal: '', totalSessions: 10, painVas: 5 });
  const [referralBusy, setReferralBusy] = useState(false);

  useEffect(() => {
    if (patients.length && !selectedPatient) setSelectedPatient(patients[0]);
  }, [patients, selectedPatient]);

  const patientList = patients;

  const saveAssessment = async (body) => {
    await createAssessment({ ...body, assessedAt: new Date().toISOString() });
  };

  const savePlan = async (body) => {
    await createPlan({ ...body, savedAt: new Date().toISOString() });
  };

  const sessionsToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return sessions.filter((s) => (s.sessionAt || s.createdAt || '').slice(0, 10) === today).length;
  }, [sessions]);

  const addReferral = async (e) => {
    e.preventDefault();
    if (!referralForm.name.trim()) return;
    setReferralBusy(true);
    try {
      const row = await createPatient({
        ...referralForm,
        status: 'Active',
        sessions: 0,
        painVas: Number(referralForm.painVas) || 0,
        totalSessions: Number(referralForm.totalSessions) || 10,
        referredAt: new Date().toISOString(),
      });
      setSelectedPatient(row);
      setShowReferral(false);
      setReferralForm({ name: '', diagnosis: '', referredBy: '', goal: '', totalSessions: 10, painVas: 5 });
    } finally { setReferralBusy(false); }
  };

  return (
    <PageShell title="Physiotherapy" subtitle="Assessment · Treatment Plans · Session Log · Outcome Tracking · Exercise Library">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Active Patients', val: patientList.filter(p => p.status === 'Active').length, color: '#10b981' }, { label: 'Sessions Today', val: sessionsToday, color: '#6366f1' }, { label: 'Avg VAS Score', val: patientList.length ? (patientList.reduce((s, p) => s + (p.painVas || 0), 0) / patientList.length).toFixed(1) : '—', color: '#f59e0b' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Patients</div>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowReferral((s) => !s)}>{showReferral ? '✕' : '+'}</button>
          </div>
          {showReferral && (
            <form className="card mb" onSubmit={addReferral} style={{ padding: 12, borderLeft: '3px solid #0d6e6a' }}>
              <div className="field mb"><label>Name *</label><input className="input" required value={referralForm.name} onChange={(e) => setReferralForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="field mb"><label>Diagnosis</label><input className="input" value={referralForm.diagnosis} onChange={(e) => setReferralForm((f) => ({ ...f, diagnosis: e.target.value }))} /></div>
              <div className="field mb"><label>Referred by</label><input className="input" value={referralForm.referredBy} onChange={(e) => setReferralForm((f) => ({ ...f, referredBy: e.target.value }))} /></div>
              <div className="field mb"><label>Goal</label><input className="input" value={referralForm.goal} onChange={(e) => setReferralForm((f) => ({ ...f, goal: e.target.value }))} /></div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={referralBusy}>{referralBusy ? '…' : 'Add referral'}</button>
            </form>
          )}
          {patientList.length === 0 && !showReferral && <p className="hint table-empty">No physiotherapy referrals yet. Tap + to add a patient referral.</p>}
          {patientList.map(p => (
            <div key={p.id || p.recordId} onClick={() => setSelectedPatient(p)} style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${selectedPatient?.id === p.id || selectedPatient?.recordId === p.recordId ? '#0d6e6a' : '#e2e8f0'}`, background: selectedPatient?.id === p.id || selectedPatient?.recordId === p.recordId ? '#f0fdfa' : '#fff', marginBottom: 8, transition: 'all 0.2s' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{p.diagnosis}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 5 }}>
                  <div style={{ width: `${(p.sessions / p.totalSessions) * 100}%`, background: '#0d6e6a', height: 5, borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{p.sessions}/{p.totalSessions}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Pain VAS:</span>
                <span style={{ fontSize: 13, fontWeight: 900, color: p.painVas <= 3 ? '#10b981' : p.painVas <= 6 ? '#f59e0b' : '#dc2626' }}>{p.painVas}/10</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
            {[{ id: 'assessment', label: '📋 Assessment' }, { id: 'plan', label: '🏋 Treatment Plan' }, { id: 'sessions', label: '📝 Session Log' }].map(({ id, label }) => (
              <button key={id} type="button" className={`emr-chip ${subTab === id ? 'active' : ''}`} onClick={() => setSubTab(id)}>{label}</button>
            ))}
          </div>
          {subTab === 'assessment' && <AssessmentTab patient={selectedPatient} onSave={saveAssessment} />}
          {subTab === 'plan' && <TreatmentPlanTab patient={selectedPatient} savedPlans={plans} onSavePlan={savePlan} />}
          {subTab === 'sessions' && <SessionLogTab patient={selectedPatient} sessions={sessions} onCreateSession={createSession} onPatchPatient={patchPatient} />}
        </div>
      </div>
    </PageShell>
  );
}
